import { ArrowLeft, AlertTriangle, TrendingDown, TrendingUp, CheckCircle2 } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from 'vite-react-ssg';
import { Card, CardContent } from "@/components/ui/card";
import NavigationButtons from "@/components/NavigationButtons";
import RelatedLinks from "@/components/RelatedLinks";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";

const DeepseekChatReasonerRetiredBilling = () => {
  const flash = DEEPSEEK_PRICING.models["v4-flash"];
  const pro = DEEPSEEK_PRICING.models["v4-pro"];
  const navigate = useNavigate();
  const canonical =
    "https://deepseek.ai/blog/deepseek-chat-reasoner-retired-billing-impact";

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "deepseek-chat and deepseek-reasoner Retired: Both Map to V4-Flash — Migrate to V4-Pro and Your Bill Triples",
    datePublished: "2026-07-25T09:00:00+00:00",
    dateModified: "2026-07-26T09:00:00+00:00",
    author: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "DeepSeek retired deepseek-chat and deepseek-reasoner on July 24, 2026 at 15:59 UTC. Both aliases map to deepseek-v4-flash, so a correct migration is price-neutral — switching to deepseek-v4-pro costs about 3.1x more. Verified rates and worked examples.",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://deepseek.ai/blog" },
      {
        "@type": "ListItem",
        position: 3,
        name: "deepseek-chat & deepseek-reasoner Retired — Billing Impact",
        item: canonical,
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "When exactly did deepseek-chat and deepseek-reasoner stop working?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Both legacy aliases were fully retired on July 24, 2026 at 15:59 UTC. Calls to those model IDs now return an error and must be pointed at deepseek-v4-flash, which is the model both aliases mapped to.",
        },
      },
      {
        "@type": "Question",
        name: "Which model do deepseek-chat and deepseek-reasoner map to?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Both map to deepseek-v4-flash. Per DeepSeek's API docs, deepseek-chat maps to V4-Flash in non-thinking mode and deepseek-reasoner maps to V4-Flash in thinking mode. Neither alias maps to deepseek-v4-pro.",
        },
      },
      {
        "@type": "Question",
        name: "Is this a rename or a real price change?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "If you migrate as documented — both aliases to deepseek-v4-flash — your per-token price is unchanged: $0.14 input (cache miss), $0.0028 input (cache hit), $0.28 output per 1M tokens. The price change only happens if you assume deepseek-v4-pro is the successor to deepseek-reasoner. Pro costs $0.435 / $0.003625 / $0.87, which is roughly 3.1x more for the same traffic.",
        },
      },
      {
        "@type": "Question",
        name: "Does DeepSeek still offer off-peak discounts?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. The off-peak discount belonged to V3/R1 and was retired with those aliases. Instead, DeepSeek announced a peak surcharge (rates double during Beijing 09:00–12:00 and 14:00–18:00, i.e. UTC 01:00–04:00 and 06:00–10:00) tied to the official V4 release. As of July 25, 2026 the surcharge is announced but not active in the official price list.",
        },
      },
      {
        "@type": "Question",
        name: "Do I need to change my SDK or endpoint?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "No. The base URL (https://api.deepseek.com) and OpenAI-compatible schema are unchanged. Only the model string changes: deepseek-chat → deepseek-v4-flash (non-thinking), deepseek-reasoner → deepseek-v4-flash (thinking mode).",
        },
      },
      {
        "@type": "Question",
        name: "Will my costs go up or down after migrating?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Migrated correctly, they stay exactly the same. A workload of 500M input tokens (30% cache hits) and 100M output tokens costs about $77/month on deepseek-v4-flash — the same as before, because both retired aliases were billed at the Flash rates. Switching that same workload to deepseek-v4-pro costs about $240/month, a 3.1x increase.",
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>deepseek-reasoner Retired: Migrate to V4-Flash, Not Pro</title>
        <meta
          name="description"
          content="deepseek-chat and deepseek-reasoner retired July 24, 2026. Both map to deepseek-v4-flash — a correct migration costs the same. Picking deepseek-v4-pro costs 3.1x more. Verified rates."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:title"
          content="deepseek-reasoner Retired: The Migration Most Guides Get Backwards"
        />
        <meta
          property="og:description"
          content="Verified July 26, 2026: both retired aliases map to deepseek-v4-flash. Migrate as documented and your price is unchanged; migrate to V4-Pro and it triples."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-6 text-slate-600 hover:text-[#0066FF]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0066FF]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#0066FF]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">
              deepseek-chat &amp; deepseek-reasoner Retired
            </span>
          </nav>

          <div className="mb-6 inline-flex items-center gap-2 text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200 px-3 py-1.5 rounded-full">
            <AlertTriangle className="h-3.5 w-3.5" />
            Breaking · Verified July 25, 2026 · Independent Guide
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
            deepseek-chat and deepseek-reasoner Are Gone — Migrate to the Wrong Model and Your Bill Triples
          </h1>

          <p className="text-lg text-slate-600 mb-2">
            By the Deep Seek AI editorial desk · July 25, 2026 · 8 min read
          </p>
          <p className="text-base text-slate-500 mb-8 italic">
            Every other migration guide tells you to rename a string. Most of them rename it
            to the wrong thing. Both retired aliases map to{" "}
            <strong>deepseek-v4-flash</strong> — migrate that way and your price is
            identical. Assume V4-Pro is the reasoner's successor and you pay about{" "}
            <strong>3.1x more</strong> for the same tokens.
          </p>

          <Card className="mb-8 border-red-200 bg-red-50/60">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-lg font-bold text-red-900 mb-2 mt-0">
                    What broke, and when
                  </h2>
                  <p className="text-sm text-red-900 mb-2">
                    On <strong>July 24, 2026 at 15:59 UTC</strong>, DeepSeek permanently
                    disabled the two legacy model aliases that most production integrations
                    still pointed at:
                  </p>
                  <ul className="text-sm text-red-900 space-y-1 list-disc pl-5">
                    <li><code className="bg-red-100 px-1.5 py-0.5 rounded">deepseek-chat</code> — the default alias in most quick-start snippets and SDK examples</li>
                    <li><code className="bg-red-100 px-1.5 py-0.5 rounded">deepseek-reasoner</code> — the reasoning-tier alias for chain-of-thought workloads</li>
                  </ul>
                  <p className="text-sm text-red-900 mt-3">
                    Calls to either ID now return an error. There is no grace period and
                    no soft redirect.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
            The documented mapping (both aliases go to the same model)
          </h2>
          <p className="text-slate-700 mb-4">
            The mechanical change is trivial. Base URL, request schema, streaming format,
            tool-calling contract — all unchanged. But note where both aliases land: per
            DeepSeek's API docs, <code>deepseek-chat</code> maps to{" "}
            <strong>V4-Flash in non-thinking mode</strong> and{" "}
            <code>deepseek-reasoner</code> maps to{" "}
            <strong>V4-Flash in thinking mode</strong>. Neither maps to V4-Pro.
          </p>

          <div className="bg-slate-900 text-slate-100 rounded-lg p-5 mb-6 font-mono text-sm overflow-x-auto">
            <div className="text-slate-400 mb-2">// Before (broken as of July 24, 15:59 UTC)</div>
            <div>model: <span className="text-red-400">"deepseek-chat"</span></div>
            <div>model: <span className="text-red-400">"deepseek-reasoner"</span></div>
            <div className="text-slate-400 mt-4 mb-2">// After — same model, mode is a request setting</div>
            <div>model: <span className="text-emerald-400">"deepseek-v4-flash"</span> <span className="text-slate-500">// non-thinking</span></div>
            <div>model: <span className="text-emerald-400">"deepseek-v4-flash"</span> <span className="text-slate-500">// thinking mode</span></div>
            <div className="text-slate-400 mt-4 mb-2">// NOT the migration target</div>
            <div>model: <span className="text-red-400">"deepseek-v4-pro"</span> <span className="text-slate-500">// ~3.1x the cost</span></div>
          </div>

          <p className="text-slate-700 mb-8">
            That distinction is the whole story. Migrate as documented and your bill is
            unchanged. Reach for <code>deepseek-v4-pro</code> because it looks like the
            successor to <code>deepseek-reasoner</code> and you have just tripled your
            per-token cost without changing a single prompt.
          </p>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
            The billing impact, both ways
          </h2>
          <p className="text-slate-700 mb-6">
            All rates below are per 1,000,000 tokens, USD, verified against{" "}
            <a
              href="https://api-docs.deepseek.com/quick_start/pricing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0066FF] underline"
            >
              api-docs.deepseek.com/quick_start/pricing
            </a>{" "}
            on July 25, 2026.
          </p>

          <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
            Both aliases → deepseek-v4-flash (price-neutral)
          </h3>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Component</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">deepseek-chat / -reasoner (old)</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">deepseek-v4-flash (new)</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Delta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-4 py-3">Input (cache miss)</td>
                  <td className="text-right px-4 py-3">${flash.inputCacheMiss}</td>
                  <td className="text-right px-4 py-3">${flash.inputCacheMiss}</td>
                  <td className="text-right px-4 py-3 text-slate-500">flat</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Input (cache hit)</td>
                  <td className="text-right px-4 py-3">${flash.inputCacheHit}</td>
                  <td className="text-right px-4 py-3">${flash.inputCacheHit}</td>
                  <td className="text-right px-4 py-3 text-slate-500">flat</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Output</td>
                  <td className="text-right px-4 py-3">${flash.output}</td>
                  <td className="text-right px-4 py-3">${flash.output}</td>
                  <td className="text-right px-4 py-3 text-slate-500">flat</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-600 mb-8 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" />
            <span>
              Verdict: <strong>no price change at all.</strong> Both retired aliases were
              served by the same V4-Flash rate card, so a documented migration is exactly
              cost-neutral. Thinking mode (former <code>deepseek-reasoner</code>) is a
              request-level setting on V4-Flash, not a more expensive model.
            </span>
          </p>

          <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
            The expensive mistake: assuming V4-Pro is the reasoner successor
          </h3>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-slate-700">Component</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">deepseek-v4-flash</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">deepseek-v4-pro</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-700">Delta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="px-4 py-3">Input (cache miss)</td>
                  <td className="text-right px-4 py-3">${flash.inputCacheMiss}</td>
                  <td className="text-right px-4 py-3">${pro.inputCacheMiss}</td>
                  <td className="text-right px-4 py-3 text-red-700 font-semibold">+211%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Input (cache hit)</td>
                  <td className="text-right px-4 py-3">${flash.inputCacheHit}</td>
                  <td className="text-right px-4 py-3">${pro.inputCacheHit}</td>
                  <td className="text-right px-4 py-3 text-red-700 font-semibold">+29%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Output</td>
                  <td className="text-right px-4 py-3">${flash.output}</td>
                  <td className="text-right px-4 py-3">${pro.output}</td>
                  <td className="text-right px-4 py-3 text-red-700 font-semibold">+211%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm text-slate-600 mb-8 flex items-start gap-2">
            <TrendingUp className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <span>
              Verdict: <strong>roughly 3.1x more expensive</strong> for identical traffic.
              V4-Pro is a bigger model (1.6T total / 49B active vs 284B / 13B) and may be
              worth it — but it is a capability upgrade you choose, not the migration
              target the docs prescribe.
            </span>
          </p>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
            Worked example: a mid-sized production chatbot
          </h2>
          <p className="text-slate-700 mb-4">
            Assume 500M input tokens/month with a 30% cache-hit rate (350M cache miss,
            150M cache hit) and 100M output tokens/month:
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 mb-6 text-sm">
            <div className="mb-3">
              <div className="font-semibold text-slate-900 mb-1">
                Correct migration (deepseek-v4-flash) — and the old alias price
              </div>
              <div className="text-slate-700 font-mono">
                (350M × ${flash.inputCacheMiss} + 150M × ${flash.inputCacheHit} + 100M × ${flash.output}) / 1M ={" "}
                <strong>$77.42/month</strong>
              </div>
            </div>
            <div>
              <div className="font-semibold text-slate-900 mb-1">
                Wrong assumption (deepseek-v4-pro)
              </div>
              <div className="text-slate-700 font-mono">
                (350M × ${pro.inputCacheMiss} + 150M × ${pro.inputCacheHit} + 100M × ${pro.output}) / 1M ={" "}
                <strong>$239.79/month</strong>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-200 text-red-700 font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Difference: +$162/month (3.1x) for the same tokens — from one wrong model string.
            </div>
          </div>
          <p className="text-slate-700 mb-8">
            Run your own numbers with our{" "}
            <Link to="/pricing" className="text-[#0066FF] underline font-medium">
              interactive DeepSeek pricing calculator
            </Link>
            .
          </p>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
            What about off-peak discounts and the surge pricing?
          </h2>
          <p className="text-slate-700 mb-4">
            This is where most competing migration guides get it wrong, because they read
            the V3-era docs. Two things you need to know as of July 25, 2026:
          </p>
          <ul className="list-disc pl-6 space-y-3 text-slate-700 mb-6">
            <li>
              <strong>The old off-peak discount is gone.</strong> It was a V3/R1 feature
              and was retired together with those aliases. V4-Flash and V4-Pro do not
              currently offer an off-peak discount.
            </li>
            <li>
              <strong>Peak surge pricing is announced but not yet active.</strong> On June
              30 DeepSeek announced that rates <strong>double</strong> during two daily
              windows — Beijing 09:00–12:00 and 14:00–18:00 (UTC 01:00–04:00 and
              06:00–10:00) — tied to the official V4 release, which was announced for
              mid-July 2026. The official price list still shows a single flat rate and no
              confirmed switch-over date. We check this weekly and will update this page
              the day it flips.
            </li>
          </ul>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-8 text-sm text-blue-900">
            <strong>Practical implication:</strong> if you handle async or batch
            workloads (embeddings, evals, background summarisation), you have a short
            window — probably weeks — to move them to overnight UTC+8 schedules before
            the surcharge activates. Doing it now is free insurance.
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
            30-minute migration checklist
          </h2>
          <ol className="space-y-3 text-slate-700 mb-8">
            {[
              "Grep your codebase for 'deepseek-chat' and 'deepseek-reasoner' — check hardcoded strings, config files, environment variables, and any prompt-management tools (LangSmith, Helicone, Langfuse).",
              "Replace both with 'deepseek-v4-flash'. That is the documented mapping for each alias: deepseek-chat → V4-Flash non-thinking, deepseek-reasoner → V4-Flash thinking mode.",
              "Do NOT reach for 'deepseek-v4-pro' just because it sounds like the reasoner successor. Same traffic on Pro costs about 3.1x more ($0.435/$0.87 vs $0.14/$0.28 per 1M tokens). Pick Pro deliberately, for capability, not by default.",
              "Set thinking mode explicitly where you relied on deepseek-reasoner: V4-Flash serves both modes, so the reasoning behaviour is a request-level choice now, not a model choice.",
              "Re-check downstream schema assumptions: the reasoning_content field is still returned in thinking mode, so parsers written for deepseek-reasoner keep working.",
              "Leave your cost model alone if you migrated to Flash — the rates are identical. If you moved anything to Pro, re-baseline your FinOps alerts upward by ~3x before they page you.",
              "Move async/batch jobs to hours outside Beijing 09:00–12:00 and 14:00–18:00 now, before the announced peak surcharge activates.",
              "Deploy behind a feature flag, roll out at 10% → 50% → 100% while watching latency and output-quality regressions.",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
                <span>{step}</span>
              </li>
            ))}
          </ol>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
            What most guides get wrong
          </h2>
          <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-8">
            <li>
              Telling you <code>deepseek-reasoner</code> becomes{" "}
              <code>deepseek-v4-pro</code>. The docs map it to{" "}
              <strong>V4-Flash in thinking mode</strong>. Following that advice triples
              your bill for identical traffic.
            </li>
            <li>
              Framing the migration as a saving. Done correctly it is price-neutral;
              there is no discount to collect.
            </li>
            <li>
              Still quoting the "V3 off-peak 50% discount." That window closed with V3/R1.
            </li>
            <li>
              Treating the announced peak surcharge as already-active. As of July 25,
              2026 it is not.
            </li>
          </ul>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
            FAQ
          </h2>
          <div className="space-y-4 mb-10">
            {faqSchema.mainEntity.map((q, i) => (
              <details
                key={i}
                className="border border-slate-200 rounded-lg p-4 group"
              >
                <summary className="font-semibold text-slate-900 cursor-pointer">
                  {q.name}
                </summary>
                <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                  {q.acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>

          <Card className="mb-10 bg-[#0066FF]/5 border-[#0066FF]/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <TrendingUp className="h-5 w-5 text-[#0066FF] flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2 mt-0">
                    Related independent guides
                  </h3>
                  <ul className="text-sm text-slate-700 space-y-1 list-disc pl-5">
                    <li>
                      <Link to="/pricing" className="text-[#0066FF] underline">
                        DeepSeek pricing — verified rates + interactive calculator
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/blog/deepseek-v4-ga-surge-pricing-migration"
                        className="text-[#0066FF] underline"
                      >
                        DeepSeek V4 GA & the first surge pricing model in AI
                      </Link>
                    </li>
                    <li>
                      <Link to="/deepseek-api" className="text-[#0066FF] underline">
                        DeepSeek API — endpoints, auth, and SDK overview
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/blog/how-to-use-deepseek-v4-better-than-99-percent"
                        className="text-[#0066FF] underline"
                      >
                        How to use DeepSeek V4 better than 99% of people
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-slate-500 italic border-t border-slate-200 pt-6 mb-8">
            This is an independent guide published by the Deep Seek AI editorial desk.
            We are not affiliated with DeepSeek.com. All rates verified against the
            official pricing page on July 25, 2026 and will be re-verified weekly.
          </p>

          <RelatedLinks />
          <NavigationButtons />
        </div>
      </div>
    </>
  );
};

export default DeepseekChatReasonerRetiredBilling;
